/**
 * Common file manager utilities shared between MediaLibrary and Files pages
 *
 * This module provides shared GraphQL queries, WebDAV operations, and UI utilities
 * for file management functionality across the application.
 *
 */

/// <reference path="../types/web/third_party/Swal.d.ts" />
/// <reference path="../types/web/b6p/index.d.ts" />

import { GqlClient, type GqlResponse } from './gql/GqlClient.js';
import type { RightsFlags } from './filemanager-rights.js';

/**
 * Get the csrf object, waiting briefly if not yet available due to script loading race condition
 * @returns Promise resolving to csrf object
 * @throws Error if csrf is not available after waiting
 */
async function getCsrf(): Promise<{ fetch: (url: string, options?: RequestInit) => Promise<Response> }> {
  // Check if already available
  if (typeof csrf !== 'undefined' && csrf !== null) {
    return csrf;
  }

  // Wait up to 1 second for csrf to become available
  for (let i = 0; i < 20; i++) {
    await new Promise(resolve => setTimeout(resolve, 50));
    if (typeof csrf !== 'undefined' && csrf !== null) {
      return csrf;
    }
  }

  throw new Error('csrf is not defined - dom.js may not have loaded');
}

/**
 * IMG_ROOT global variable from common_js.jsp
 * Points to versioned static image resources for browser caching
 */
declare const IMG_ROOT: string;

// ============================================================================
// GraphQL Interfaces
// ============================================================================

/**
 * GraphQL folder representation from server
 */
export interface GraphQLFolder extends RightsFlags {
  id: string;
  shortId: string;
  folderName: string;
  root: boolean;
  hasChildren: boolean;
  fileCount: number;
  hasAnyLock?: boolean;
}

/**
 * GraphQL document representation from server
 */
export interface GraphQLDocument extends RightsFlags {
  id: string;
  shortId: string;
  documentName: string;
  author?: string;
  mimeType: string;
  fileSize: number;
  iconURL: string;
  thumbnailPreviewURL?: string;
  downloadURL: string;
  hasAnyLock?: boolean;
  modifyDateMillis?: number;
}

/**
 * PathElement can be either a folder or document
 */
export type GraphQLPathElement = GraphQLFolder | GraphQLDocument;

/**
 * GraphQL query result for folder contents. The listing queries read `folderChildren` under the alias
 * `children`, so this shape is the one every view already consumes.
 */
export interface FolderContentsResult {
  data: {
    children: (RightsFlags & {
      id: string;
      shortId: string;
      __typename: string;
      folderName?: string;
      documentName?: string;
      displayName?: string;
      root?: boolean;
      hasChildren?: boolean;
      fileCount?: number;
      author?: string;
      mimeType?: string;
      fileSize?: number;
      iconURL?: string;
      thumbnailPreviewURL?: string;
      downloadURL?: string;
      hasAnyLock?: boolean;
      modifyDateMillis?: number;
      hasFieldLinks?: boolean;
      description?: string;
    })[];
  };
}

// ============================================================================
// UI State Interfaces
// ============================================================================

/**
 * UI representation of a path element (folder or file)
 */
export interface PathElementUI extends RightsFlags {
  id: string;
  name: string;
  type: 'folder' | 'file';
  iconURL?: string;
  thumbnailPreviewURL?: string;
  downloadURL?: string;
  fileSize?: number;
  mimeType?: string;
  modifyDateMillis?: number;
  hasFieldLinks?: boolean;
  hasAnyLock?: boolean;
}

/**
 * Breadcrumb item for navigation
 */
export interface BreadcrumbItem extends RightsFlags {
  id: string;
  name: string;
  isRoot: boolean;
}

/**
 * Pagination state for file loading
 */
export interface PaginationState {
  folderId: string;
  filesPerPage: number;
  currentPage: number;
  hasMoreFiles: boolean;
  isLoading: boolean;
}

/**
 * View mode type
 */
export type ViewMode = 'icon' | 'list' | 'tree';

/**
 * Drag and drop state management
 */
export interface DragState {
  elements: PathElementUI[];
  targetFolder: string | null;
}

/**
 * File search result item
 */
export interface FileSearchItem {
  id: string;
  name: string;
  path: string;
  isFolder: boolean;
  iconURL?: string;
  fileSize?: number;
  parentFolderId: string;
  description?: string;
}

/**
 * File search result
 */
export interface FileSearchResult {
  items: FileSearchItem[];
  total: number;
  returned: number;
  hasMore: boolean;
}

// ============================================================================
// GraphQL Queries
// ============================================================================

/**
 * GraphQL query to get folders in a parent folder
 */
export const FOLDERS_QUERY = `
  query GetFolders($folderId: String!) {
    children: folderChildren(folderId: $folderId, kind: FOLDERS) {
      id
      shortId
      __typename
      ... on FolderInterface {
        folderName
        displayName
        root
        hasChildren
        fileCount
        hasAnyLock
        canEdit
        canRename
        canMove
        canDelete
        canManageLock
        canAddChild
        canReorder
      }
    }
  }
`;

/**
 * GraphQL query to get files in a parent folder with pagination
 */
export const FILES_QUERY = `
  query GetFiles($folderId: String!, $start: Int!, $count: Int!) {
    children: folderChildren(folderId: $folderId, kind: DOCUMENTS, start: $start, count: $count) {
      id
      shortId
      __typename
      ... on DocumentInterface {
        documentName
        displayName
        author
        mimeType
        fileSize
        iconURL
        thumbnailPreviewURL
        downloadURL
        hasAnyLock
        canEdit
        canRename
        canMove
        canDelete
        canManageLock
        modifyDateMillis
        description
      }
      ... on DocumentVersionsInterface {
        hasFieldLinks
      }
    }
  }
`;

/**
 * GraphQL query to resolve a library (or Shared Files page) ID to its root folder; a folder ID answers itself
 */
export const LIBRARY_FOLDER_QUERY = `
  query GetLibraryFolder($id: String!) {
    folder(id: $id) {
      id
    }
  }
`;

/**
 * GraphQL query to get a folder's shortId
 */
export const SHORT_ID_QUERY = `
  query GetFolderShortId($id: String!) {
    folder(id: $id) {
      shortId
    }
  }
`;

/**
 * GraphQL query to get folder with parent chain for breadcrumb building.
 * canAddChild is requested at every level so breadcrumbs know which folders accept uploads and drops.
 */
export const FOLDER_PATH_QUERY = `
  query GetFolderPath($id: String!) {
    folder(id: $id) {
      id
      ... on FolderInterface {
        folderName
        displayName
        root
        canAddChild
        parents {
          id
          ... on FolderInterface {
            folderName
            displayName
            root
            canAddChild
            parents {
              id
              ... on FolderInterface {
                folderName
                displayName
                root
                canAddChild
                parents {
                  id
                  ... on FolderInterface {
                    folderName
                    displayName
                    root
                    canAddChild
                    parents {
                      id
                      ... on FolderInterface {
                        folderName
                        displayName
                        root
                        canAddChild
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * GraphQL query for a single folder's own rights, for a folder no loaded listing describes (the root)
 */
export const FOLDER_RIGHTS_QUERY = `
  query GetFolderRights($id: String!) {
    folder(id: $id) {
      id
      canAddChild
    }
  }
`;

/**
 * GraphQL mutation to delete items
 */
export const DELETE_MUTATION = `
  mutation DeleteItem($ids: [String!]!) {
    deleteRemoteObject(ids: $ids)
  }
`;

/**
 * GraphQL mutation to move/reorder children within a container. It answers the container as stored
 * after the write; its `rowVersion` is the stamp a later write on the container must carry.
 */
export const MOVE_CHILDREN_MUTATION = `
  mutation MoveChildren($containerId: String!, $itemIds: [String!]!, $beforeId: String, $afterId: String) {
    moveChildren(containerId: $containerId, itemIds: $itemIds, beforeId: $beforeId, afterId: $afterId) { rowVersion }
  }
`;

/**
 * GraphQL query to search files and folders recursively
 */
export const SEARCH_FILES_QUERY = `
  query SearchFiles($folderId: String!, $search: String!, $searchMode: SearchMode, $recursive: Boolean, $start: Int, $count: Int) {
    searchFiles(folderId: $folderId, search: $search, searchMode: $searchMode, recursive: $recursive, start: $start, count: $count) {
      items {
        id
        name
        path
        isFolder
        iconURL
        fileSize
        parentFolderId
        description
      }
      total
      returned
      hasMore
    }
  }
`;

/**
 * GraphQL query to get all children (folders + files) with pagination for tree view
 */
export const ALL_CHILDREN_QUERY = `
  query GetAllChildren($folderId: String!, $start: Int!, $count: Int!) {
    children: folderChildren(folderId: $folderId, kind: ALL, start: $start, count: $count) {
      id
      shortId
      __typename
      ... on FolderInterface {
        folderName
        displayName
        root
        hasChildren
        fileCount
        hasAnyLock
        canEdit
        canRename
        canMove
        canDelete
        canManageLock
        canAddChild
        canReorder
      }
      ... on DocumentInterface {
        documentName
        displayName
        author
        mimeType
        fileSize
        iconURL
        thumbnailPreviewURL
        downloadURL
        hasAnyLock
        canEdit
        canRename
        canMove
        canDelete
        canManageLock
        modifyDateMillis
        description
      }
      ... on DocumentVersionsInterface {
        hasFieldLinks
      }
    }
  }
`;

// ============================================================================
// GraphQL Operations
// ============================================================================

/**
 * Module-private GraphQL client, lazily constructed once `csrf` is available
 * (see {@link getCsrf}). Override via {@link setGqlClient} for tests.
 */
let _client: GqlClient | null = null;

async function getClient(): Promise<GqlClient> {
  if (_client) { return _client; }
  const csrfObj = await getCsrf();
  _client = new GqlClient(csrfObj, '/gql');
  return _client;
}

/**
 * Inject a custom {@link GqlClient} (primarily for tests).
 */
export function setGqlClient(client: GqlClient): void {
  _client = client;
}

/**
 * Execute a GraphQL query. Throws on HTTP failure or GraphQL-level errors.
 * Returns the raw response envelope cast to `T` — callers historically type
 * `T` as `{ data: {...} }`, which this function preserves.
 * @param query GraphQL query string
 * @param variables Query variables
 * @returns Query result
 */
export async function executeGraphQL<T>(query: string, variables: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const client = await getClient();
  const result = await client.execute<GqlResponse<unknown>>({ query, variables, signal });

  if (result.errors) {
    console.error('GraphQL errors:', result.errors);
    throw new Error(`GraphQL error: ${result.errors[0]?.message || 'Unknown error'}`);
  }

  return result as unknown as T;
}

/**
 * Resolve a MediaLibrary ID to its root folder ID
 * @param libraryId MediaLibrary ID
 * @returns Root folder ID
 */
export async function resolveLibraryToFolder(libraryId: string): Promise<string> {
  try {
    const result = await executeGraphQL<{
      data: {
        folder?: {
          id: string;
          folder?: { id: string };
        };
      };
    }>(LIBRARY_FOLDER_QUERY, { id: libraryId });

    // Check if it's a MediaLibrary with a folder property
    if (result.data?.folder?.id) {
      return result.data.folder.id;
    }

    // Otherwise assume it's already a folder ID
    return libraryId;
  } catch (error) {
    console.error('Failed to resolve library to folder:', error);
    // Fall back to using the ID as-is
    return libraryId;
  }
}

/**
 * Get shortId for a folder via GraphQL
 * @param folderId Folder ID
 * @returns Short ID for WebDAV URLs
 */
export async function getFolderShortId(folderId: string): Promise<string> {
  const result = await executeGraphQL<{
    data: { folder?: { shortId: string } };
  }>(SHORT_ID_QUERY, { id: folderId });

  if (!result.data?.folder?.shortId) {
    throw new Error('Failed to get folder shortId');
  }

  return result.data.folder.shortId;
}

/**
 * Get shortId for a move target via GraphQL. The target is always a folder (or a library or Shared Files page,
 * which answers its root folder's shortId).
 * @param objectId Folder ID
 * @returns Short ID for WebDAV URLs
 */
export async function getObjectShortId(objectId: string): Promise<string> {
  return getFolderShortId(objectId);
}

/**
 * Load folders from a parent folder
 * @param folderId Parent folder ID
 * @returns Array of folder items
 */
export async function loadFolders(folderId: string): Promise<FolderContentsResult['data']['children']> {
  const result = await executeGraphQL<FolderContentsResult>(FOLDERS_QUERY, { folderId });
  return result.data?.children || [];
}

/**
 * Load files from a parent folder with pagination
 * @param folderId Parent folder ID
 * @param start Starting index
 * @param count Number of items to fetch
 * @returns Array of file items
 */
export async function loadFiles(
  folderId: string,
  start: number,
  count: number
): Promise<FolderContentsResult['data']['children']> {
  const result = await executeGraphQL<FolderContentsResult>(FILES_QUERY, {
    folderId,
    start,
    count
  });
  return result.data?.children || [];
}

/**
 * Load all children (folders + files) from a parent folder with pagination.
 * Used by tree view to load all items in a single query.
 * @param folderId Parent folder ID
 * @param start Starting index
 * @param count Number of items to fetch
 * @returns Array of folder and file items
 */
export async function loadAllChildren(
  folderId: string,
  start: number,
  count: number
): Promise<FolderContentsResult['data']['children']> {
  const result = await executeGraphQL<FolderContentsResult>(ALL_CHILDREN_QUERY, {
    folderId,
    start,
    count
  });
  return result.data?.children || [];
}

/**
 * Folder path node from GraphQL query
 */
interface FolderPathNode extends RightsFlags {
  id: string;
  folderName?: string;
  displayName?: string;
  root?: boolean;
  parents?: FolderPathNode[];
}

/**
 * Get folder breadcrumb trail by fetching the folder and its parent chain.
 * Returns breadcrumbs from root to the target folder.
 * Recursively queries if folder depth exceeds 4 levels to get complete path.
 * @param folderId Folder ID to get path for
 * @returns Array of BreadcrumbItem from root to target folder
 */
export async function getFolderBreadcrumbs(folderId: string): Promise<BreadcrumbItem[]> {
  const result = await executeGraphQL<{ data: { folder: FolderPathNode | null } }>(FOLDER_PATH_QUERY, {
    id: folderId
  });

  const folder = result.data?.folder;
  if (!folder) {
    return [];
  }

  // Build the path by traversing up the parent chain
  const breadcrumbs: BreadcrumbItem[] = [];

  // Recursive function to flatten the parent chain and return topmost node
  function collectParents(node: FolderPathNode, depth: number): FolderPathNode {
    if (node.parents && node.parents.length > 0) {
      // Go up to parent first (so we build root-to-leaf order)
      const topmost = collectParents(node.parents[0], depth + 1);
      // Add this node to breadcrumbs
      breadcrumbs.push({
        id: node.id,
        name: node.folderName || node.displayName || 'Folder',
        isRoot: node.root || false,
        canAddChild: node.canAddChild
      });
      return topmost;
    } else {
      // This is the topmost node in our query result
      breadcrumbs.push({
        id: node.id,
        name: node.folderName || node.displayName || 'Folder',
        isRoot: node.root || false,
        canAddChild: node.canAddChild
      });
      return node;
    }
  }

  const topmostNode = collectParents(folder, 0);

  // If we have 4+ levels and the topmost node is not a root, we hit the depth limit
  // Recursively query for the topmost node's parents
  if (!topmostNode.root && breadcrumbs.length >= 4) {
    const parentBreadcrumbs = await getFolderBreadcrumbs(topmostNode.id);
    // Remove the duplicate topmost node and prepend parent breadcrumbs
    if (parentBreadcrumbs.length > 0) {
      breadcrumbs.shift(); // Remove duplicate topmost node
      return [...parentBreadcrumbs, ...breadcrumbs];
    }
  }

  return breadcrumbs;
}

/**
 * Get whether the viewer may add files and folders to a folder.
 * @param folderId Folder ID to check
 * @returns The right, or undefined when the server did not report it
 */
export async function getFolderCanAddChild(folderId: string): Promise<boolean | undefined> {
  const result = await executeGraphQL<{ data: { folder: { canAddChild?: boolean } | null } }>(FOLDER_RIGHTS_QUERY, {
    id: folderId
  });
  return result.data?.folder?.canAddChild ?? undefined;
}

/**
 * Delete an item via GraphQL mutation
 * @param itemId Item to delete
 */
export async function deleteItem(itemId: string): Promise<void> {
  await executeGraphQL(DELETE_MUTATION, { ids: [itemId] });
}

/**
 * Search for files and folders recursively within a folder hierarchy
 * @param folderId Root folder ID to search from
 * @param search Search term
 * @param options Search options
 * @returns Search results
 */
export async function searchFiles(
  folderId: string,
  search: string,
  options: {
    searchMode?: 'CONTAINS' | 'REGEX' | 'GLOB';
    recursive?: boolean;
    start?: number;
    count?: number;
    signal?: AbortSignal;
  } = {}
): Promise<FileSearchResult> {
  const {
    searchMode = 'CONTAINS',
    recursive = true,
    start = 0,
    count = 50,
    signal
  } = options;

  try {
    const result = await executeGraphQL<{
      data: { searchFiles: FileSearchResult };
    }>(SEARCH_FILES_QUERY, {
      folderId,
      search,
      searchMode,
      recursive,
      start,
      count
    }, signal);

    if (!result.data?.searchFiles) {
      showError('Search failed. Please try again.');
      return { items: [], total: 0, returned: 0, hasMore: false };
    }

    return result.data.searchFiles;
  } catch (error) {
    // Don't show errors for aborted requests
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { items: [], total: 0, returned: 0, hasMore: false };
    }
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showError(`Search failed: ${errorMessage}`);
    return { items: [], total: 0, returned: 0, hasMore: false };
  }
}

/**
 * Move/reorder children within a container
 * @param containerId Parent container ID
 * @param itemIds IDs of items to move
 * @param beforeId Insert before this item (optional)
 * @param afterId Insert after this item (optional)
 * @returns The container's rowVersion after the move: one higher than before when the order
 *   changed, unchanged when the call left it as it was
 */
export async function moveChildren(
  containerId: string,
  itemIds: string[],
  beforeId?: string,
  afterId?: string
): Promise<number> {
  const result = await executeGraphQL<{ data: { moveChildren: { rowVersion: number } } }>(
    MOVE_CHILDREN_MUTATION,
    {
      containerId,
      itemIds,
      beforeId: beforeId || null,
      afterId: afterId || null
    }
  );
  return result.data.moveChildren.rowVersion;
}

// ============================================================================
// WebDAV Operations
// ============================================================================

/**
 * Create a new folder via WebDAV MKCOL
 * @param parentFolderId Parent folder ID
 * @param folderName Name for the new folder
 * @returns True if successful
 */
export async function createFolder(parentFolderId: string, folderName: string): Promise<boolean> {
  const csrfObj = await getCsrf();
  const parentShortId = await getFolderShortId(parentFolderId);
  const webdavUrl = `/files/${parentShortId}/${encodeURIComponent(folderName)}`;

  const response = await csrfObj.fetch(webdavUrl, {
    method: 'MKCOL'
  });

  if (!response.ok) {
    throw new Error(webdavFailureMessage('Create folder', response.status, response.statusText,
      await response.text().catch(() => '')));
  }

  return true;
}

/**
 * Move item to different folder via WebDAV MOVE
 * @param itemName Name of the item (filename or folder name)
 * @param sourceFolderId Source folder ID
 * @param targetFolderId Destination folder ID
 */
export async function moveItem(
  itemName: string,
  sourceFolderId: string,
  targetFolderId: string
): Promise<void> {
  const csrfObj = await getCsrf();
  const sourceShortId = await getFolderShortId(sourceFolderId);
  const targetShortId = await getObjectShortId(targetFolderId);

  const sourceUrl = `/files/${sourceShortId}/${encodeURIComponent(itemName)}`;
  const destUrl = `/files/${targetShortId}/${encodeURIComponent(itemName)}`;

  const response = await csrfObj.fetch(sourceUrl, {
    method: 'MOVE',
    headers: {
      'Destination': destUrl,
      // Fail (412) rather than silently clobber a same-named file already in the
      // destination folder — the server defaults the WebDAV Overwrite header to true.
      'Overwrite': 'F'
    }
  });

  if (!response.ok) {
    throw new Error(webdavFailureMessage('Move', response.status, response.statusText,
      await response.text().catch(() => ''), MOVE_FAILURE_REASONS));
  }
}

/**
 * Only surface a response body in a user-facing error when it's short, plain
 * text — never a container HTML error page or a WebDAV `<D:error>` XML document,
 * both of which the server can return for a failed request.
 */
function sanitizeErrorBody(body: string): string {
  const trimmed = body.trim();
  if (!trimmed || trimmed.startsWith('<')) {
    return '';
  }
  return trimmed.length > 200 ? `${trimmed.slice(0, 200)}…` : trimmed;
}

/**
 * Per-status reasons for a failed PUT (the statuses the server sends bodyless).
 * A 403 here is an AddChild denial on the destination folder — the single most
 * common upload failure, and the one that used to reach the user as an empty
 * message.
 */
export const UPLOAD_FAILURE_REASONS: Record<number, string> = {
  403: 'You do not have permission to upload to this folder.',
  404: 'The destination folder no longer exists.',
  409: 'The destination folder could not be found.',
  423: 'The file is locked and cannot be replaced.',
  507: 'There is not enough storage space to save this file.',
};

/**
 * Per-status reasons for a failed MOVE. A permission refusal arrives with a plain-text body naming
 * the missing right (the item, the source folder or the destination), which takes precedence; the
 * 403 entry is only the fallback for a bodyless one.
 */
const MOVE_FAILURE_REASONS: Record<number, string> = {
  403: 'You do not have permission to move this item.',
  404: 'The item or destination folder no longer exists.',
  409: 'The destination folder could not be found.',
  412: 'An item with that name already exists in the destination folder.',
  423: 'The item is locked by another user and cannot be moved.',
};

/**
 * Human-readable reason for a failed WebDAV request. `statusText` is empty over
 * HTTP/2, so prefer a sanitized plain-text body, then a status-specific message,
 * then the raw status.
 */
export function webdavFailureMessage(
  operation: string,
  status: number,
  statusText: string,
  body: string,
  reasons: Record<number, string> = {},
): string {
  const reason = sanitizeErrorBody(body) || reasons[status] || statusText || `HTTP ${status}`;
  return `${operation} failed (${status}): ${reason}`;
}

/**
 * Rename an item via GraphQL updateRemoteObject mutation
 * @param itemId ID of the item to rename
 * @param newName New display name
 */
export async function renameItem(
  itemId: string,
  newName: string
): Promise<void> {
  await executeGraphQL(
    `mutation RenameItem($id: String!, $name: String!) {
      updateRemoteObject(inputs: [{ topId: $id, displayName: $name }]) {
        displayName
      }
    }`,
    { id: itemId, name: newName }
  );
}

/**
 * Get CSRF token for the current origin from sessionStorage.
 * This reads from the same storage (csrf.SESSION_STORAGE_KEY) used by csrf.fetch() in dom.js.
 * @returns The CSRF token or null if not available
 */
function getCsrfToken(): string | null {
  const tokensStr = sessionStorage.getItem(csrf.SESSION_STORAGE_KEY);
  if (!tokensStr) {
    return null;
  }
  try {
    const tokens = JSON.parse(tokensStr);
    return tokens[location.origin] || null;
  } catch {
    return null;
  }
}

/**
 * Upload a single file via WebDAV PUT with progress callback
 * @param file File to upload
 * @param folderId Target folder ID
 * @param onProgress Progress callback (0-100)
 */
export async function uploadFile(
  file: File,
  folderId: string,
  onProgress?: (percent: number) => void
): Promise<void> {
  const shortId = await getFolderShortId(folderId);
  const webdavUrl = `/files/${shortId}/${encodeURIComponent(file.name)}`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Progress tracking
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percent = (e.loaded / e.total) * 100;
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(webdavFailureMessage('Upload', xhr.status, xhr.statusText, xhr.responseText || '',
          UPLOAD_FAILURE_REASONS)));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.open('PUT', webdavUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    // Add CSRF token - read from csrf.SESSION_STORAGE_KEY storage (same as csrf.fetch in dom.js)
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      xhr.setRequestHeader('B6P-CSRF-TOKEN', csrfToken);
    }

    xhr.send(file);
  });
}

// ============================================================================
// Security Utilities
// ============================================================================

/**
 * Escape HTML special characters to prevent XSS attacks.
 * Use this when inserting user-provided text into innerHTML.
 * @param text Text to escape (handles null, undefined, and non-string values)
 * @returns HTML-escaped text safe for innerHTML
 */
export function escapeHtml(text: string | null | undefined): string {
  if (text == null) return '';
  const div = document.createElement('div');
  div.textContent = String(text);
  return div.innerHTML;
}

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format file size from bytes to human-readable string
 * @param bytes File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null) {
    return '';
  }

  if (bytes < 1024) {
    return bytes + ' B';
  }

  if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(1) + ' KB';
  }

  if (bytes < 1024 * 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

/**
 * Format timestamp to readable date/time string
 * @param millis Timestamp in milliseconds
 * @returns Formatted date/time string
 */
export function formatDateTime(millis: number | undefined): string {
  if (millis === undefined || millis === null) {
    return '';
  }

  const date = new Date(millis);
  // Format as MM/DD/YYYY HH:MM AM/PM
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours);

  return `${month}/${day}/${year} ${hoursStr}:${minutes} ${ampm}`;
}

/**
 * Format timestamp to date-only string
 * @param millis Timestamp in milliseconds
 * @returns Formatted date string
 */
export function formatDate(millis: number | undefined): string {
  if (millis === undefined || millis === null) {
    return '';
  }

  const date = new Date(millis);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}

// ============================================================================
// Dialog Helpers
// ============================================================================

/**
 * Open a popup dialog and return a promise that resolves when it closes
 * @param url URL to open
 * @param windowName Window name
 * @param width Window width
 * @param height Window height
 * @returns Promise that resolves when popup closes
 */
export function openPopupDialog(
  url: string,
  windowName: string,
  width: number,
  height: number
): Promise<void> {
  return new Promise((resolve) => {
    // Center popup relative to current browser window, not primary monitor
    // This ensures correct positioning on multi-monitor setups
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`;

    const popup = window.open(url, windowName, features);

    if (popup) {
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          resolve();
        }
      }, 500);
    } else {
      // Popup was blocked
      resolve();
    }
  });
}

/**
 * Show error message to user using SweetAlert or fallback to alert
 * @param message Error message
 */
export function showError(message: string): void {
  if (typeof Swal !== 'undefined') {
    Swal.fire({
      title: 'Error',
      text: message,
      type: 'error'
    });
  } else {
    alert(message);
  }
}

/**
 * Prompt user for text input using SweetAlert
 * @param title Dialog title
 * @param placeholder Input placeholder
 * @param defaultValue Default input value
 * @param selectUpToExtension If true, selects filename up to last period (for renaming), otherwise selects all
 * @returns User input or null if cancelled
 */
export async function promptInput(
  title: string,
  placeholder: string,
  defaultValue: string = '',
  selectUpToExtension: boolean = false
): Promise<string | null> {
  if (typeof Swal !== 'undefined') {
    const result = await Swal.fire({
      title,
      input: 'text',
      inputValue: defaultValue,
      inputPlaceholder: placeholder,
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      inputValidator: (value: string) => {
        if (!value || !value.trim()) {
          return 'Value is required';
        }
        return null;
      },
      onOpen: () => {
        // Select the default text for easy replacement
        setTimeout(() => {
          const input = document.querySelector('.swal2-input') as HTMLInputElement;
          if (input) {
            if (selectUpToExtension) {
              // Select up to the last period (file extension)
              const lastDotIndex = input.value.lastIndexOf('.');
              if (lastDotIndex > 0) {
                // Found extension, select up to it
                input.setSelectionRange(0, lastDotIndex);
              } else {
                // No extension, select all
                input.select();
              }
            } else {
              // Select all text
              input.select();
            }
            input.focus();
          }
        }, 100);
      }
    });

    return result.value ? result.value.trim() : null;
  } else {
    const result = prompt(title, defaultValue);
    return result ? result.trim() : null;
  }
}

/**
 * Confirm action with user
 * @param message Confirmation message
 * @returns True if confirmed
 */
export function confirmAction(message: string): boolean {
  return confirm(message);
}

// ============================================================================
// URL Builders
// ============================================================================

/**
 * Build URL for edit permissions dialog
 * @param itemId Item ID
 * @returns URL string
 */
export function buildEditPermissionsUrl(itemId: string): string {
  return `/shared/files/editpermissions.jsp?_event=edit&_id=${encodeURIComponent(itemId)}`;
}

/**
 * Build URL for edit file dialog
 * @param itemId Item ID
 * @returns URL string
 */
export function buildEditFileUrl(itemId: string): string {
  return `/shared/files/editfile_simple.jsp?_event=edit&_id=${encodeURIComponent(itemId)}`;
}

/**
 * Build URL for lock management dialog
 * @param itemId Item ID
 * @returns URL string
 */
export function buildLockUrl(itemId: string): string {
  return `/shared/files/managelock.jsp?_event=manageLock&_id=${encodeURIComponent(itemId)}`;
}

/**
 * Build URL for slideshow
 * @param folderId Folder ID
 * @returns URL string
 */
export function buildSlideshowUrl(folderId: string): string {
  return `/shared/files/medialibrary_slides.jsp?_event=view&_id=${encodeURIComponent(folderId)}`;
}

/**
 * Build URL for reorder files dialog
 * @param folderId Folder ID
 * @returns URL string
 */
export function buildReorderFilesUrl(folderId: string): string {
  return `/shared/admin/reorderchildren.jsp?_event=reorder&_id=${encodeURIComponent(folderId)}&_k=123320&noRefresh=true`;
}

/**
 * Build URL for reorder folders dialog
 * @param folderId Folder ID
 * @returns URL string
 */
export function buildReorderFoldersUrl(folderId: string): string {
  return `/shared/admin/reorderchildren.jsp?_event=reorder&_id=${encodeURIComponent(folderId)}&_k=530001&noRefresh=true`;
}

/**
 * Convert a /download/ URL to an /inline/ URL for safe inline file display.
 * @param downloadURL The original download URL (e.g. /download/123456/file.pdf)
 * @returns The inline URL (e.g. /inline/123456/file.pdf)
 */
export function toInlineUrl(downloadURL: string): string {
  return downloadURL.replace(/^\/download\//, '/inline/');
}

/**
 * Lazy, concurrency-limited thumbnail loader. Only fires the request once
 * an image scrolls near the viewport, and caps the number of in-flight
 * preview requests so a folder with many files does not hammer
 * DocumentPreviewServlet and exhaust the server's DB connection pool.
 *
 * Usage: set img.dataset.thumbSrc to the real URL, leave src pointing at a
 * placeholder icon, then call thumbnailLoader.observe(img).
 */
export class LazyThumbnailLoader {
  private observer: IntersectionObserver | null = null;
  private readonly queue: HTMLImageElement[] = [];
  private readonly tracked: WeakSet<HTMLImageElement> = new WeakSet();
  private inFlight = 0;

  constructor(
    private readonly maxConcurrent: number = 4,
    private readonly rootMargin: string = '200px'
  ) {}

  observe(img: HTMLImageElement): void {
    if (this.tracked.has(img)) { return; }
    this.tracked.add(img);
    if (typeof IntersectionObserver === 'undefined') {
      const src = img.dataset.thumbSrc;
      if (src) { img.src = src; }
      return;
    }
    if (this.observer === null) {
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLImageElement;
            this.observer!.unobserve(target);
            this.queue.push(target);
            this.drain();
          }
        }
      }, { rootMargin: this.rootMargin });
    }
    this.observer.observe(img);
  }

  /**
   * Release all observed images and clear the pending queue. Callers should
   * invoke this before re-rendering a view to avoid retaining references to
   * DOM nodes that have been detached.
   */
  disconnect(): void {
    if (this.observer !== null) {
      this.observer.disconnect();
    }
    this.queue.length = 0;
  }

  private drain(): void {
    while (this.inFlight < this.maxConcurrent && this.queue.length > 0) {
      const img = this.queue.shift()!;
      const src = img.dataset.thumbSrc;
      if (!src) { continue; }
      this.inFlight++;
      const finish = (): void => {
        this.inFlight--;
        this.drain();
      };
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
      img.src = src;
    }
  }
}

export const thumbnailLoader = new LazyThumbnailLoader();
